import { createReducer, on } from '@ngrx/store';

import { LiveData } from '../../models/api';
import { LiveContentFilter, LiveFilter } from '../../models/common';
import { ExternalLive, Live, Player } from '../../models/home';
import * as HomeActions from './home.actions';

export const homeFeatureKey = 'home';

export interface State {
  ui: UIState;
  livesRaw: LiveData[];
  lives: Live[];
  connectionCount?: number;
  externalLives: ExternalLive[];
  players: Player[];
  error: null;
}
export interface UIState {
  isShowingTopbar: boolean;
  isShowingButtons: boolean;
  filter: LiveFilter;
  contentFilter: LiveContentFilter;
}

export const initialState: State = {
  ui: {
    isShowingTopbar: true,
    isShowingButtons: true,
    filter: {
      upcoming: false,
      jp: true,
      id: true,
      kr: true,
      en: true,
      holo: false,
      vr: false,
      other: false,
      bilibili: false,
      private: false,
    },
    contentFilter: {
      ARK: true,
      Apex: true,
      Fall: true,
      Mine: true,
      Etc: true,
    },
  },
  livesRaw: [],
  lives: [],
  externalLives: [],
  players: [],
  error: null,
};

export const reducer = createReducer(
  initialState,

  on(HomeActions.loadLives, (state) => state),
  on(HomeActions.loadLivesSuccess, (state, { livesRaw, connectionCount }) => ({
    ...state,
    livesRaw,
    connectionCount,
    lives: updateLives(state.lives, livesRaw),
  })),
  on(HomeActions.loadLivesFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(HomeActions.addExternalLive, (state, { videoId, platform }) => ({
    ...state,
    externalLives: [
      ...state.externalLives,
      {
        videoId,
        background:
          platform === 'twitch'
            ? `https://static-cdn.jtvnw.net/previews-ttv/live_user_${videoId}-320x180.jpg`
            : platform === 'youtube'
            ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`
            : platform === 'cas' // FIXME: bg for cas
            ? ''
            : '',
        selected: false,
        platform,
      } as ExternalLive,
    ],
  })),

  on(HomeActions.removeExternalLive, (state, { videoId }) => ({
    ...state,
    externalLives: state.externalLives.filter((l) => l.videoId !== videoId),
    players: state.players.filter((p) => p.videoId !== videoId),
  })),

  on(HomeActions.toggleChat, (state, { videoId }) => ({
    ...state,
    players: state.players.map((p) =>
      p.videoId === videoId ? { ...p, showingChat: !p.showingChat } : p,
    ),
  })),

  on(HomeActions.toggleTopbar, (state) => ({
    ...state,
    ui: {
      ...state.ui,
      isShowingTopbar: !state.ui.isShowingTopbar,
    },
  })),

  on(HomeActions.toggleButtons, (state) => ({
    ...state,
    ui: {
      ...state.ui,
      isShowingButtons: !state.ui.isShowingButtons,
    },
  })),

  on(HomeActions.toggleFilter, (state, { filterType }) => {
    const ui: UIState = {
      ...state.ui,
      filter: {
        ...state.ui.filter,
        [filterType]: !state.ui.filter[filterType],
      },
    };
    return { ...state, ui };
  }),

  on(HomeActions.toggleContentFilter, (state, { contentType }) => {
    const ui: UIState = {
      ...state.ui,
      contentFilter: {
        ...state.ui.contentFilter,
        [contentType]: !state.ui.contentFilter[contentType],
      },
    };
    return { ...state, ui };
  }),

  on(HomeActions.turnOff, (state, { videoId }) => {
    const players = state.players.filter((p) => p.videoId !== videoId);
    const lives = state.lives.map((live) =>
      live.videoId === videoId
        ? ({ ...live, selected: !live.selected } as Live)
        : live,
    );
    const externalLives = state.externalLives.map((exLive) =>
      exLive.videoId === videoId
        ? ({ ...exLive, selected: !exLive.selected } as ExternalLive)
        : exLive,
    );

    return { ...state, players, lives, externalLives };
  }),

  on(HomeActions.turnOn, (state, { videoId, platform }) => {
    const playing = true; // auto play
    const players = state.players.concat({
      videoId,
      playing,
      showingChat: false,
      platform,
      casId: platform === 'cas' ? videoId.replace(/^cas_/, '') : videoId,
    } as Player);
    const lives = state.lives.map((live) =>
      live.videoId === videoId
        ? ({ ...live, selected: !live.selected } as Live)
        : live,
    );
    const externalLives = state.externalLives.map((exLive) =>
      exLive.videoId === videoId
        ? ({ ...exLive, selected: !exLive.selected } as ExternalLive)
        : exLive,
    );

    return { ...state, players, lives, externalLives };
  }),

  on(HomeActions.selectOne, (state, { videoId, platform }) => {
    const playing = true; // auto play
    const player = state.players.find((p) => p.videoId === videoId);

    if (player) {
      // unselect all
      const players: Player[] = [];
      const lives = state.lives.map(
        (live) => ({ ...live, selected: false } as Live),
      );
      const externalLives = state.externalLives.map(
        (exLive) => ({ ...exLive, selected: false } as ExternalLive),
      );
      return { ...state, players, lives, externalLives };
    } else {
      // select one
      const players: Player[] = [
        {
          videoId,
          playing,
          showingChat: false,
          platform,
          casId: videoId.replace(/^cas_/, ''),
        },
      ];
      const lives = state.lives.map((live) =>
        live.videoId === videoId
          ? ({ ...live, selected: true } as Live)
          : ({ ...live, selected: false } as Live),
      );
      const externalLives = state.externalLives.map((exLive) =>
        exLive.videoId === videoId
          ? ({ ...exLive, selected: true } as ExternalLive)
          : ({ ...exLive, selected: false } as ExternalLive),
      );
      return { ...state, players, lives, externalLives };
    }
  }),

  on(HomeActions.loadSetting, (state, { setting }) => ({
    ...state,
    ui: {
      ...state.ui,
      filter: setting.filter,
      isShowingButtons: setting.isShowingButtons,
    },
  })),
);

const updateLives = (prevs: Live[], livesRaw: LiveData[]): Live[] => {
  const fresh: Live[] = livesRaw.map(
    ({
      author,
      channel,
      videoId,
      title,
      avatar,
      groups,
      type,
      publishedAt,
      viewer,
      isPremiere,
      thumb,
      isTwitch,
      isCas,
      isBilibili,
    }) => {
      let background: string;
      if (isBilibili) {
        background = `/assets/imgs/bilibili/cover/${channel}.webp`;
      } else if (thumb) {
        background = thumb;
      } else if (isTwitch) {
        background = `https://static-cdn.jtvnw.net/previews-ttv/live_user_${videoId}-320x180.jpg`;
      } else {
        background = `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
      }

      return {
        author,
        channel,
        videoId,
        title,
        background,
        avatar: isBilibili
          ? `/assets/imgs/bilibili/face/${channel}.webp`
          : avatar,
        selected: false,
        groups,
        type,
        publishedAt: publishedAt?._seconds
          ? new Date(publishedAt?._seconds * 1000)
          : null,
        viewer,
        isPremiere,
        isTwitch,
        isCas,
        isBilibili,
      };
    },
  );

  for (const prev of prevs.filter((p) => p.selected)) {
    const live = fresh.find((f) => f.videoId === prev.videoId);
    if (live) {
      // restorer selected status
      live.selected = prev.selected;
    } else {
      // remain selected lives
      fresh.push(prev);
    }
  }

  return fresh;
};
